/**
 * @namespace Stores
 * @category Stores
 * @module containers-store
 * */

import api from '@/api'
import i18n from '@/i18n'
import Vue from 'vue'
import APIUrls from '@/config/simpliciti-apis.js'

function newState() {
  return {
    isSyncInProgress: false,
    flows: [],
    uses: [], // should be "in_progress" and "deposited"
    states: [], // should be "empty", "full" and "reusable"
    containers: [],
    containersTypes: [],
    currentContainer: [],
  }
}

function addLanguage(options = {}) {
  if (options.payload) {
    options.payload.lang = i18n.locale
  } else {
    options.payload = { lang: i18n.locale }
  }
  return options
}

export default {
  namespaced: true,
  state: newState(),
  getters: {
    allContainers: (state) => state.containers,
    currentContainer: (state) => state.currentContainer,
    dropDownFlowList: (state) => {
      let flowItems = []
      if (state.flows.length > 0) {
        flowItems = state.flows.map((item) => {
          return { name: item.label, value: item.id }
        })
      }
      return flowItems
    },
    dropDownStateList: (state) => {
      let stateItems = []
      if (state.states.length > 0) {
        stateItems = state.states.map((item) => {
          return { name: item.label, value: item.id, code: item.code }
        })
      }
      return stateItems
    },
    dropDownStateLimitedList: (state) => {
      let stateItems = []
      if (state.states.length > 0) {
        stateItems = state.states
          .filter((item) => item.code === 'empty') // only "empty"
          .map((item) => {
            return { name: item.label, value: item.id, code: item.code }
          })
      }
      return stateItems
    },
    dropDownUseList: (state) => {
      let useItems = []
      if (state.uses.length > 0) {
        useItems = state.uses.map((item) => {
          return {
            name: item.label,
            value: item.id,
            code: item.code,
          }
        })
      }
      return useItems
    },
    dropDownUseLimitedList: (state) => {
      let useItems = []
      if (state.uses.length > 0) {
        useItems = state.uses
          .filter((item) => item.code !== 'in_progress') // but "in progress"
          .map((item) => {
            return {
              name: item.label,
              value: item.id,
              code: item.code,
            }
          })
      }
      return useItems
    },
    getStateById: (state) => (idState) => {
      return state.states.find((item) => item.id == idState)
    },
    findCodeInContainers: (state) => (code) => {
      return state.containers.find((item) => item.code == code)
    },
  },
  mutations: {
    resetStore(state) {
      Object.assign(state, newState())
    },
    isSyncInProgress: (state, value) => (state.isSyncInProgress = value),
    resetContainersStore: (state) => (state.containers = []),
  },
  actions: {
    resetStore({ commit }) {
      commit('resetStore')
    },
    async getContainers({ state, commit, dispatch }, options = {}) {
      commit('isSyncInProgress', true)
      options = addLanguage(options)
      state.containers = await api.container.getAllPooling({
        ...options,
      })
      state.containers = state.containers.map((item) => {
        if (typeof item._links.flux1 != 'undefined') {
          item.flux1Id = item._links.flux1.href.substring(
            api.APIUrls.APIV3_GEORED_FLUX_MATERIALS.length + 1
          )
          item.flux1Id = parseInt(item.flux1Id)
        }
        if (typeof item._links.flux2 != 'undefined') {
          item.flux2Id = item._links.flux2.href.substring(
            api.APIUrls.APIV3_GEORED_FLUX_MATERIALS.length + 1
          )
          item.flux2Id = parseInt(item.flux2Id)
        }
        if (typeof item._links.lastState !== 'undefined') {
          item.stateId = item._links.lastState.href.substring(
            api.APIUrls.APIV3_GEORED_CONTAINER_STATES.length + 1
          )
          item.stateId = parseInt(item.stateId)
        }
        if (typeof item.lastUseLabel !== 'undefined') {
          item.lastUseId = state.uses.find(
            (elt) => elt.label == item.lastUseLabel
          ).id
          item.lastUseId = parseInt(item.lastUseId)
        }
        return item
      })
      dispatch(
        'simpliciti_map/setDataset',
        { type: 'containersMarkers', data: state.containers },
        { root: true }
      )
      commit('isSyncInProgress', false)
    },
    async getContainerTypes({ state, commit }) {
      commit('isSyncInProgress', true)
      let options = addLanguage()
      state.containersTypes = await api.container.getAllPooling({
        ...options,
      })
      commit('isSyncInProgress', false)
    },
    async getContainersFlows({ state, commit }) {
      commit('isSyncInProgress', true)
      let options = addLanguage()
      state.flows = await api.containerFlow.getAllPooling({
        ...options,
      })
      commit('isSyncInProgress', false)
    },
    async getContainersStates({ state, commit }) {
      commit('isSyncInProgress', true)
      let options = addLanguage()
      state.states = await api.containerState.getAllPooling({
        ...options,
      })
      commit('isSyncInProgress', false)
    },
    async getContainersUses({ state, commit }) {
      commit('isSyncInProgress', true)
      let options = addLanguage()
      state.uses = await api.containerUses.getAllPooling({
        ...options,
      })
      commit('isSyncInProgress', false)
    },
    async addContainer({ state, commit, dispatch }, payload) {
      commit('isSyncInProgress', true)
      let params = {}
      Object.keys(payload).forEach(function (key) {
        if (payload[key] !== '' && key) {
          let obj = {}
          obj[key] = payload[key]
          params = Object.assign(params, obj)
        }
      })

      try {
        let response = await api.v3.post(
          api.APIUrls.APIV3_GEORED_CONTAINER_CONTAINERS,
          params
        )
        commit('resetContainersStore')
        await dispatch('getContainers')
      } catch (error) {
        console.log('Error : ', error.message)
        commit('isSyncInProgress', false)
        return false
      }
      commit('isSyncInProgress', false)
      return true
    },
    async updateContainer({ state, commit, dispatch }, payload) {
      commit('isSyncInProgress', true)
      // needToReload permet d'eviter un reload si d'autres actions àç faire derrière
      let needToReload = true
      if (typeof payload['needToReload'] != 'undefined') {
        needToReload = payload.needToReload
      }
      let params = {}
      Object.keys(payload).forEach(function (key) {
        if (key) {
          let obj = {}
          obj[key] = payload[key]
          params = Object.assign(params, obj)
        }
      })
      try {
        let response = await api.v3.patch(
          api.APIUrls.APIV3_GEORED_CONTAINER_CONTAINERS + '/' + params.id,
          params
        )
        commit('resetContainersStore')
        if (needToReload) {
          await dispatch('getContainers')
        }
      } catch (error) {
        console.log('Error : ', error.message)
        commit('isSyncInProgress', false)
        return false
      }
      commit('isSyncInProgress', false)
      return true
    },
    async archiveContainer({ commit, dispatch }, payload) {
      commit('isSyncInProgress', true)
      let params = {}
      Object.keys(payload).forEach(function (key) {
        if (key) {
          let obj = {}
          obj[key] = payload[key]
          params = Object.assign(params, obj)
        }
      })
      try {
        let response = await api.v3.delete(
          api.APIUrls.APIV3_GEORED_CONTAINER_CONTAINERS + '/' + params.id
        )
        commit('resetContainersStore')
        await dispatch('getContainers')
      } catch (error) {
        console.log('Error : ', error.message)
        commit('isSyncInProgress', false)
        return false
      }
      commit('isSyncInProgress', false)
    },
    async unarchiveContainer({ commit, dispatch }, options = {}) {
      commit('isSyncInProgress', true)
      let params = {}
      Object.keys(options).forEach(function (key) {
        if (key) {
          let obj = {}
          obj[key] = options[key]
          params = Object.assign(params, obj)
        }
      })
      let url = api.APIUrls.APIV3_GEORED_CONTAINER_CONTAINERS_UNDELETE.split(
        '${id}'
      ).join(params.id)
      params['payload'] = { 'exists[deletedAt]': true }
      try {
        let response = await api.v3.patch(url)
        commit('resetContainersStore')
        await dispatch('getContainers', params)
      } catch (error) {
        console.log('Error : ', error.message)
        commit('isSyncInProgress', false)
        return false
      }
      commit('isSyncInProgress', false)
      return true
    },
    async addUseContainer({ commit, dispatch }, payload) {
      commit('isSyncInProgress', true)
      let params = {}
      Object.keys(payload).forEach(function (key) {
        if (key) {
          let obj = {}
          obj[key] = payload[key]
          params = Object.assign(params, obj)
        }
      })
      let response = {}
      try {
        response = await api.v3.post(
          api.APIUrls.APIV3_GEORED_CONTAINER_USE_CONTAINERS,
          params
        )
        commit('resetContainersStore')
        await dispatch('getContainers')
      } catch (error) {
        console.log('Error : ', error.message)
        console.log(response)
        commit('isSyncInProgress', false)

        return { error: 'Error Machin' }
      }
      commit('isSyncInProgress', false)
      return true
    },
  },
}
